import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { ModalProps, StyleSheet, Text, View } from 'react-native';
import Svg, { Line } from 'react-native-svg';

import { testProps } from '../../../config';
import GradientPlate, { GradientStops } from '../common/GradientPlate';
import { Pressable } from '../common/Touchable';
import { SOCIAL_FONT } from '../Social/socialTheme';
import RedesignModal from './RedesignModal';
import { useRedesignScale } from './useRedesignScale';

/**
 * A yes/no dialog in the redesign popups' chrome (session confirmation, logout). There is no Figma
 * frame for it; it is drawn like the Social filter popup: `RedesignModal` over the dimmed, blurred
 * live screen, the app backdrop inside a gold rim, a red close disc on the corner, and a red
 * cancel / green confirm pair of gradient buttons. Numbers are design units, scaled once.
 */
const PANEL = { height: 236, titleHeight: 34, radius: 17, rim: 2 };
const LAYOUT = {
	paddingTop: 46,
	sideText: 28,
	sideButtons: 22,
	buttonsTop: 22,
	buttonWidth: 151,
	buttonHeight: 44,
};
const CLOSE = { size: 37, right: -7, top: 1 };

const colors = {
	title: '#FFCF5A',
	text: '#FFF8F4',
	panelRim: '#FFC261',
	panelTint: 'rgba(8,0,0,0.5)',
	scrim: 'rgba(0,0,0,0.68)',
} as const;

const CANCEL_FILL: GradientStops = [
	['0', '#B22306'],
	['0.5', '#6E0900'],
	['1', '#A00C00'],
];
const CANCEL_RIM: GradientStops = [
	['0', '#FFEA96'],
	['0.5', '#FFB844'],
	['1', '#FF7F23'],
];
const CONFIRM_FILL: GradientStops = [
	['0', '#00CC3A'],
	['0.5', '#00B330'],
	['1', '#009E22'],
];
const CONFIRM_RIM: GradientStops = [
	['0', '#5CF57E'],
	['0.5', '#1CEE4F'],
	['1', '#2EAC42'],
];

/** Keeps an icon above its absolutely positioned GradientPlate on web (see FriendsFilterModal). */
const IconBox = ({ children }: { children: React.ReactNode }) => (
	<View pointerEvents='none'>{children}</View>
);

type Props = {
	title?: string;
	message: string;
	/** Omitted for a notice: the confirm button alone, centred. */
	cancelLabel?: string;
	confirmLabel: string;
	/** Cancel button, close disc and a tap on the scrim. */
	onCancel: () => void;
	onConfirm: () => void;
	cancelTestId?: string;
	confirmTestId?: string;
	supportedOrientations?: ModalProps['supportedOrientations'];
};

export const RedesignConfirmModal = ({
	title,
	message,
	cancelLabel,
	confirmLabel,
	onCancel,
	onConfirm,
	cancelTestId,
	confirmTestId,
	supportedOrientations,
}: Props) => {
	const { t } = useTranslation();
	const panelHeight = PANEL.height + (title ? PANEL.titleHeight : 0);
	const scale = useRedesignScale({ fitHeight: panelHeight });
	const s = (n: number) => n * scale;
	const styles = useMemo(() => createStyles(scale), [scale]);

	const closeButton = (
		<Pressable
			accessibilityRole='button'
			accessibilityLabel={t('T_SITE_ACHIEVEMENTS_CLOSE', { defaultValue: 'Close' })}
			onPress={onCancel}
			hitSlop={6}
			style={styles.close}
		>
			<GradientPlate
				width={s(CLOSE.size)}
				height={s(CLOSE.size)}
				radius={s(CLOSE.size / 2)}
				stops={CANCEL_FILL}
				strokeStops={CANCEL_RIM}
				strokeWidth={Math.max(1, s(2))}
			/>
			<IconBox>
				<Svg width={s(15)} height={s(15)} viewBox='0 0 15 15'>
					<Line
						x1={2}
						y1={2}
						x2={13}
						y2={13}
						stroke='#FFEAA4'
						strokeWidth={2.8}
						strokeLinecap='round'
					/>
					<Line
						x1={13}
						y1={2}
						x2={2}
						y2={13}
						stroke='#FFEAA4'
						strokeWidth={2.8}
						strokeLinecap='round'
					/>
				</Svg>
			</IconBox>
		</Pressable>
	);

	const button = (
		label: string,
		fill: GradientStops,
		rim: GradientStops,
		glow: object,
		onPress: () => void,
		testId?: string,
	) => (
		<Pressable
			accessibilityRole='button'
			onPress={onPress}
			style={[styles.button, glow]}
			{...(testId ? testProps(testId) : null)}
		>
			<GradientPlate
				width={s(LAYOUT.buttonWidth)}
				height={s(LAYOUT.buttonHeight)}
				radius={s(10)}
				stops={fill}
				strokeStops={rim}
				strokeWidth={Math.max(1, s(1.5))}
			/>
			<Text numberOfLines={1} style={styles.buttonLabel}>
				{label.toUpperCase()}
			</Text>
		</Pressable>
	);

	return (
		<RedesignModal
			scale={scale}
			onClose={onCancel}
			panel={{
				height: panelHeight,
				radius: PANEL.radius,
				borderColor: colors.panelRim,
				borderWidth: PANEL.rim,
				tint: colors.panelTint,
				scrimColor: colors.scrim,
				overScreen: true,
			}}
			panelOverlay={closeButton}
			supportedOrientations={supportedOrientations}
		>
			<View style={styles.body}>
				{title ? (
					<Text numberOfLines={1} style={styles.title}>
						{title.toUpperCase()}
					</Text>
				) : null}

				<View style={styles.messageBox}>
					<Text style={styles.message}>{message}</Text>
				</View>

				<View style={[styles.buttons, cancelLabel ? null : styles.buttonsSingle]}>
					{cancelLabel
						? button(
								cancelLabel,
								CANCEL_FILL,
								CANCEL_RIM,
								styles.cancelGlow,
								onCancel,
								cancelTestId,
							)
						: null}
					{button(
						confirmLabel,
						CONFIRM_FILL,
						CONFIRM_RIM,
						styles.confirmGlow,
						onConfirm,
						confirmTestId,
					)}
				</View>
			</View>
		</RedesignModal>
	);
};

const createStyles = (scale: number) => {
	const s = (n: number) => n * scale;
	return StyleSheet.create({
		body: {
			flex: 1,
			paddingTop: s(LAYOUT.paddingTop),
			paddingHorizontal: s(LAYOUT.sideText),
			alignItems: 'center',
		},
		title: {
			fontFamily: SOCIAL_FONT,
			fontSize: s(22),
			lineHeight: s(26),
			fontWeight: '900',
			color: colors.title,
			textAlign: 'center',
			textShadowColor: 'rgba(80,30,0,0.6)',
			textShadowOffset: { width: 0, height: s(1) },
			textShadowRadius: s(2),
		},
		messageBox: {
			flex: 1,
			justifyContent: 'center',
		},
		message: {
			fontFamily: SOCIAL_FONT,
			fontSize: s(16),
			lineHeight: s(22),
			fontWeight: '700',
			color: colors.text,
			textAlign: 'center',
		},
		buttons: {
			width: s(358 - LAYOUT.sideButtons * 2),
			marginTop: s(LAYOUT.buttonsTop),
			marginBottom: s(LAYOUT.sideButtons),
			flexDirection: 'row',
			justifyContent: 'space-between',
		},
		buttonsSingle: {
			justifyContent: 'center',
		},
		button: {
			width: s(LAYOUT.buttonWidth),
			height: s(LAYOUT.buttonHeight),
			borderRadius: s(10),
			alignItems: 'center',
			justifyContent: 'center',
		},
		cancelGlow: {
			shadowColor: '#FF6A00',
			shadowOffset: { width: 0, height: 0 },
			shadowOpacity: 0.5,
			shadowRadius: s(8),
		},
		confirmGlow: {
			shadowColor: '#2BFF5A',
			shadowOffset: { width: 0, height: 0 },
			shadowOpacity: 0.45,
			shadowRadius: s(8),
		},
		buttonLabel: {
			fontFamily: SOCIAL_FONT,
			fontSize: s(16),
			fontWeight: '900',
			color: '#FFFFFF',
			textShadowColor: 'rgba(80,30,0,0.45)',
			textShadowOffset: { width: 0, height: s(1) },
			textShadowRadius: s(1.5),
		},
		close: {
			position: 'absolute',
			right: s(CLOSE.right),
			top: s(CLOSE.top),
			width: s(CLOSE.size),
			height: s(CLOSE.size),
			borderRadius: s(CLOSE.size / 2),
			alignItems: 'center',
			justifyContent: 'center',
			shadowColor: '#FF6A00',
			shadowOffset: { width: 0, height: 0 },
			shadowOpacity: 0.6,
			shadowRadius: s(6),
		},
	});
};

export default RedesignConfirmModal;
